/**
 * SGI (Sistema de Gestión Inmobiliaria) - Roles and Permissions Types
 *
 * Este archivo define todos los roles y permisos del sistema SGI
 * implementado según la documentación en /docs/04-modules/sgi/auth/roles-and-permissions.md
 */

// ===========================
// ROLE DEFINITIONS
// ===========================

export enum UserRole {
  // ========== ROLES INTERNOS DE OWLY ==========
  // Super administrador del sistema - CTO, CEO, Fundadores
  OWLY_SUPER_ADMIN = 'owly_super_admin',

  // Administrador general de OWLY - Product Manager, Operations Manager
  OWLY_ADMIN = 'owly_admin',

  // Equipo comercial de OWLY - Responsables de adquisición de clientes
  OWLY_SALES = 'owly_sales',

  // Soporte técnico de OWLY - Atención a clientes
  OWLY_SUPPORT = 'owly_support',

  // Equipo de desarrollo de OWLY - Ingenieros
  OWLY_DEV = 'owly_dev',

  // Business Intelligence de OWLY - Analistas de datos
  OWLY_ANALYST = 'owly_analyst',

  // Finanzas y facturación de OWLY - CFO, Finance Manager
  OWLY_FINANCE = 'owly_finance',

  // Customer Success de OWLY - AI Agent y CS Managers
  OWLY_CUSTOMER_SUCCESS = 'owly_customer_success',

  // ========== ROLES DE CLIENTES (COMPAÑÍAS) ==========
  // Administrador de compañía - Gestiona su compañía y proyectos
  COMPANY_ADMIN = 'company_admin',

  // Gestor de proyectos - Gestiona proyectos específicos
  PROJECT_MANAGER = 'project_manager',

  // Gerente de ventas - Gestiona equipo comercial
  SALES_MANAGER = 'sales_manager',

  // Asesor comercial - Gestiona leads y ventas
  SALES_AGENT = 'sales_agent',

  // Marketing - Acceso a herramientas de marketing
  MARKETING = 'marketing',

  // Solo lectura - Visualización únicamente
  VIEWER = 'viewer',

  // Legacy roles (mantener compatibilidad)
  PRESIDENT = 'president',
  MANAGER = 'manager',
  AGENT = 'agent',
}

// ===========================
// PERMISSION DEFINITIONS
// ===========================

export enum Permission {
  // ========== CONFIGURACIÓN GLOBAL ==========
  // Solo OWLY_ADMIN
  'global_config:read' = 'global_config:read',
  'global_config:write' = 'global_config:write',

  // Countries
  'countries:read' = 'countries:read',
  'countries:create' = 'countries:create',
  'countries:update' = 'countries:update',
  'countries:delete' = 'countries:delete',

  // Cities
  'cities:read' = 'cities:read',
  'cities:create' = 'cities:create',
  'cities:update' = 'cities:update',
  'cities:delete' = 'cities:delete',

  // Currencies
  'currencies:read' = 'currencies:read',
  'currencies:create' = 'currencies:create',
  'currencies:update' = 'currencies:update',
  'currencies:delete' = 'currencies:delete',

  // ========== OWLY PLATFORM (PERMISOS INTERNOS) ==========
  // Dashboard y métricas SaaS
  'owly:dashboard:view' = 'owly:dashboard:view',
  'owly:metrics:view' = 'owly:metrics:view',

  // Gestión de usuarios internos y clientes
  'owly:users:create_internal' = 'owly:users:create_internal',
  'owly:users:create_client' = 'owly:users:create_client',
  'owly:users:read' = 'owly:users:read',
  'owly:users:update' = 'owly:users:update',
  'owly:users:delete' = 'owly:users:delete',

  // Billing y planes
  'owly:billing:manage_plans' = 'owly:billing:manage_plans',
  'owly:billing:update' = 'owly:billing:update',
  'owly:billing:read' = 'owly:billing:read',

  // Sistema y configuración
  'owly:system:feature_flags' = 'owly:system:feature_flags',
  'owly:system:logs' = 'owly:system:logs',
  'owly:system:integrations' = 'owly:system:integrations',

  // Analytics SaaS
  'owly:analytics:read' = 'owly:analytics:read',
  'owly:analytics:export' = 'owly:analytics:export',
  'owly:analytics:dashboards' = 'owly:analytics:dashboards',

  // Soporte
  'owly:support:tickets' = 'owly:support:tickets',
  'owly:support:chat' = 'owly:support:chat',

  // ========== GESTIÓN DE COMPAÑÍAS ==========
  // OWLY_ADMIN, OWLY_SALES
  'companies:read' = 'companies:read',
  'companies:create' = 'companies:create',
  'companies:update' = 'companies:update',
  'companies:delete' = 'companies:delete',
  'companies:manage_users' = 'companies:manage_users',
  'companies:invite_users' = 'companies:invite_users',

  // ========== GESTIÓN DE PROYECTOS ==========
  // COMPANY_ADMIN, PROJECT_MANAGER
  'projects:read' = 'projects:read',
  'projects:create' = 'projects:create',
  'projects:update' = 'projects:update',
  'projects:delete' = 'projects:delete',
  'projects:publish' = 'projects:publish',

  // Stages & Blocks
  'stages:read' = 'stages:read',
  'stages:create' = 'stages:create',
  'stages:update' = 'stages:update',
  'stages:delete' = 'stages:delete',

  'blocks:read' = 'blocks:read',
  'blocks:create' = 'blocks:create',
  'blocks:update' = 'blocks:update',
  'blocks:delete' = 'blocks:delete',

  // Units (Inventario)
  'units:read' = 'units:read',
  'units:create' = 'units:create',
  'units:update' = 'units:update',
  'units:delete' = 'units:delete',
  'units:bulk_create' = 'units:bulk_create',
  'units:change_status' = 'units:change_status',

  // Typologies
  'typologies:read' = 'typologies:read',
  'typologies:create' = 'typologies:create',
  'typologies:update' = 'typologies:update',
  'typologies:delete' = 'typologies:delete',

  // Amenities
  'amenities:read' = 'amenities:read',
  'amenities:create' = 'amenities:create',
  'amenities:update' = 'amenities:update',
  'amenities:delete' = 'amenities:delete',

  // Price Lists
  'price_lists:read' = 'price_lists:read',
  'price_lists:create' = 'price_lists:create',
  'price_lists:update' = 'price_lists:update',
  'price_lists:delete' = 'price_lists:delete',
  'price_lists:apply' = 'price_lists:apply',

  // Media (Galleries, Tours, Rotators)
  'media:read' = 'media:read',
  'media:upload' = 'media:upload',
  'media:update' = 'media:update',
  'media:delete' = 'media:delete',

  // ========== VENTAS ==========
  // SALES_MANAGER, SALES_AGENT
  'leads:read' = 'leads:read',
  'leads:create' = 'leads:create',
  'leads:update' = 'leads:update',
  'leads:delete' = 'leads:delete',
  'leads:assign' = 'leads:assign',

  'quotes:read' = 'quotes:read',
  'quotes:create' = 'quotes:create',
  'quotes:update' = 'quotes:update',
  'quotes:delete' = 'quotes:delete',
  'quotes:approve' = 'quotes:approve',

  'sales:read' = 'sales:read',
  'sales:create' = 'sales:create',
  'sales:update' = 'sales:update',
  'sales:cancel' = 'sales:cancel',

  // ========== REPORTES ==========
  'reports:view' = 'reports:view',
  'reports:export' = 'reports:export',
  'reports:advanced' = 'reports:advanced',

  // ========== CONFIGURACIÓN ==========
  'settings:read' = 'settings:read',
  'settings:update' = 'settings:update',
}

// ===========================
// ROLE PERMISSIONS MATRIX
// ===========================

export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  // ========== OWLY_SUPER_ADMIN ==========
  [UserRole.OWLY_SUPER_ADMIN]: [
    // OWLY Platform - FULL ACCESS
    Permission['owly:dashboard:view'],
    Permission['owly:metrics:view'],
    Permission['owly:users:create_internal'],
    Permission['owly:users:create_client'],
    Permission['owly:users:read'],
    Permission['owly:users:update'],
    Permission['owly:users:delete'],
    Permission['owly:billing:manage_plans'],
    Permission['owly:billing:update'],
    Permission['owly:billing:read'],
    Permission['owly:system:feature_flags'],
    Permission['owly:system:logs'],
    Permission['owly:system:integrations'],
    Permission['owly:analytics:read'],
    Permission['owly:analytics:export'],
    Permission['owly:analytics:dashboards'],
    Permission['owly:support:tickets'],
    Permission['owly:support:chat'],

    // Configuración Global - FULL ACCESS
    Permission['global_config:read'],
    Permission['global_config:write'],
    Permission['countries:read'],
    Permission['countries:create'],
    Permission['countries:update'],
    Permission['countries:delete'],
    Permission['cities:read'],
    Permission['cities:create'],
    Permission['cities:update'],
    Permission['cities:delete'],
    Permission['currencies:read'],
    Permission['currencies:create'],
    Permission['currencies:update'],
    Permission['currencies:delete'],

    // Compañías - FULL ACCESS
    Permission['companies:read'],
    Permission['companies:create'],
    Permission['companies:update'],
    Permission['companies:delete'],
    Permission['companies:manage_users'],
    Permission['companies:invite_users'],

    // Proyectos - FULL ACCESS
    Permission['projects:read'],
    Permission['projects:create'],
    Permission['projects:update'],
    Permission['projects:delete'],
    Permission['projects:publish'],

    // Stages & Blocks
    Permission['stages:read'],
    Permission['stages:create'],
    Permission['stages:update'],
    Permission['stages:delete'],
    Permission['blocks:read'],
    Permission['blocks:create'],
    Permission['blocks:update'],
    Permission['blocks:delete'],

    // Units
    Permission['units:read'],
    Permission['units:create'],
    Permission['units:update'],
    Permission['units:delete'],
    Permission['units:bulk_create'],
    Permission['units:change_status'],

    // Typologies & Amenities
    Permission['typologies:read'],
    Permission['typologies:create'],
    Permission['typologies:update'],
    Permission['typologies:delete'],
    Permission['amenities:read'],
    Permission['amenities:create'],
    Permission['amenities:update'],
    Permission['amenities:delete'],

    // Price Lists
    Permission['price_lists:read'],
    Permission['price_lists:create'],
    Permission['price_lists:update'],
    Permission['price_lists:delete'],
    Permission['price_lists:apply'],

    // Media
    Permission['media:read'],
    Permission['media:upload'],
    Permission['media:update'],
    Permission['media:delete'],

    // Sales
    Permission['leads:read'],
    Permission['leads:create'],
    Permission['leads:update'],
    Permission['leads:delete'],
    Permission['leads:assign'],
    Permission['quotes:read'],
    Permission['quotes:create'],
    Permission['quotes:update'],
    Permission['quotes:delete'],
    Permission['quotes:approve'],
    Permission['sales:read'],
    Permission['sales:create'],
    Permission['sales:update'],
    Permission['sales:cancel'],

    // Reports
    Permission['reports:view'],
    Permission['reports:export'],
    Permission['reports:advanced'],

    // Settings
    Permission['settings:read'],
    Permission['settings:update'],
  ],

  // ========== OWLY_ADMIN ==========
  [UserRole.OWLY_ADMIN]: [
    // OWLY Platform - Full access except critical
    Permission['owly:dashboard:view'],
    Permission['owly:metrics:view'],
    Permission['owly:users:create_internal'],
    Permission['owly:users:create_client'],
    Permission['owly:users:read'],
    Permission['owly:users:update'],
    Permission['owly:billing:read'], // Solo lectura, no manage_plans
    Permission['owly:system:logs'],
    Permission['owly:system:integrations'],
    Permission['owly:analytics:read'],
    Permission['owly:analytics:export'],
    Permission['owly:analytics:dashboards'],
    Permission['owly:support:tickets'],
    Permission['owly:support:chat'],

    // Configuración Global
    Permission['global_config:read'],
    Permission['global_config:write'],
    Permission['countries:read'],
    Permission['countries:create'],
    Permission['countries:update'],
    Permission['countries:delete'],
    Permission['cities:read'],
    Permission['cities:create'],
    Permission['cities:update'],
    Permission['cities:delete'],
    Permission['currencies:read'],
    Permission['currencies:create'],
    Permission['currencies:update'],
    Permission['currencies:delete'],

    // Compañías
    Permission['companies:read'],
    Permission['companies:create'],
    Permission['companies:update'],
    Permission['companies:manage_users'],
    Permission['companies:invite_users'],

    // Analytics
    Permission['reports:view'],
    Permission['reports:export'],
    Permission['reports:advanced'],
  ],

  // ========== OWLY_SALES ==========
  [UserRole.OWLY_SALES]: [
    // OWLY Platform - Gestión comercial
    Permission['owly:dashboard:view'],
    Permission['owly:metrics:view'],
    Permission['owly:billing:update'],
    Permission['owly:billing:read'],
    Permission['owly:analytics:read'],

    // Compañías - Gestión completa
    Permission['companies:read'],
    Permission['companies:create'],
    Permission['companies:update'],
    Permission['companies:invite_users'],

    // Proyectos - Solo lectura
    Permission['projects:read'],
  ],

  // ========== OWLY_SUPPORT ==========
  [UserRole.OWLY_SUPPORT]: [
    // OWLY Platform - Soporte
    Permission['owly:dashboard:view'],
    Permission['owly:support:tickets'],
    Permission['owly:support:chat'],
    Permission['owly:system:logs'], // Solo logs no sensibles

    // Compañías - Solo lectura
    Permission['companies:read'],

    // Proyectos - Solo lectura
    Permission['projects:read'],
  ],

  // ========== OWLY_DEV ==========
  [UserRole.OWLY_DEV]: [
    // OWLY Platform - Desarrollo y debugging
    Permission['owly:dashboard:view'],
    Permission['owly:system:feature_flags'],
    Permission['owly:system:logs'],
    Permission['owly:system:integrations'],

    // Compañías - Solo lectura para debugging
    Permission['companies:read'],
  ],

  // ========== OWLY_ANALYST ==========
  [UserRole.OWLY_ANALYST]: [
    // OWLY Platform - Analytics
    Permission['owly:dashboard:view'],
    Permission['owly:metrics:view'],
    Permission['owly:analytics:read'],
    Permission['owly:analytics:export'],
    Permission['owly:analytics:dashboards'],
    Permission['owly:billing:read'],

    // Compañías - Solo datos agregados
    Permission['companies:read'],

    // Reports
    Permission['reports:view'],
    Permission['reports:export'],
    Permission['reports:advanced'],
  ],

  // ========== OWLY_FINANCE ==========
  [UserRole.OWLY_FINANCE]: [
    // OWLY Platform - Finanzas y Billing
    Permission['owly:dashboard:view'],
    Permission['owly:metrics:view'],
    Permission['owly:billing:read'],
    Permission['owly:billing:update'],
    Permission['owly:billing:manage_plans'],
    Permission['owly:analytics:read'],
    Permission['owly:analytics:export'],

    // Compañías - Gestión financiera
    Permission['companies:read'],
    Permission['companies:update'], // Para actualizar datos de billing

    // Reports financieros
    Permission['reports:view'],
    Permission['reports:export'],
    Permission['reports:advanced'],
  ],

  // ========== OWLY_CUSTOMER_SUCCESS ==========
  [UserRole.OWLY_CUSTOMER_SUCCESS]: [
    // OWLY Platform - Customer Success
    Permission['owly:dashboard:view'],
    Permission['owly:metrics:view'],
    Permission['owly:analytics:read'],
    Permission['owly:support:tickets'],
    Permission['owly:support:chat'],

    // Compañías - Gestión de clientes
    Permission['companies:read'],
    Permission['companies:update'],
    Permission['companies:invite_users'],

    // Proyectos - Para entender uso del cliente
    Permission['projects:read'],

    // Reports de uso y salud del cliente
    Permission['reports:view'],
    Permission['reports:export'],
  ],

  // ========== COMPANY_ADMIN ==========
  [UserRole.COMPANY_ADMIN]: [
    // No acceso a config global
    // Compañías - Solo su compañía
    Permission['companies:read'],
    Permission['companies:update'],
    Permission['companies:manage_users'],
    Permission['companies:invite_users'],

    // Proyectos - FULL ACCESS en su compañía
    Permission['projects:read'],
    Permission['projects:create'],
    Permission['projects:update'],
    Permission['projects:delete'],
    Permission['projects:publish'],

    // Stages & Blocks
    Permission['stages:read'],
    Permission['stages:create'],
    Permission['stages:update'],
    Permission['stages:delete'],
    Permission['blocks:read'],
    Permission['blocks:create'],
    Permission['blocks:update'],
    Permission['blocks:delete'],

    // Units
    Permission['units:read'],
    Permission['units:create'],
    Permission['units:update'],
    Permission['units:delete'],
    Permission['units:bulk_create'],
    Permission['units:change_status'],

    // Typologies & Amenities
    Permission['typologies:read'],
    Permission['typologies:create'],
    Permission['typologies:update'],
    Permission['typologies:delete'],
    Permission['amenities:read'],
    Permission['amenities:create'],
    Permission['amenities:update'],
    Permission['amenities:delete'],

    // Price Lists
    Permission['price_lists:read'],
    Permission['price_lists:create'],
    Permission['price_lists:update'],
    Permission['price_lists:delete'],
    Permission['price_lists:apply'],

    // Media
    Permission['media:read'],
    Permission['media:upload'],
    Permission['media:update'],
    Permission['media:delete'],

    // Sales
    Permission['leads:read'],
    Permission['leads:create'],
    Permission['leads:update'],
    Permission['leads:assign'],
    Permission['quotes:read'],
    Permission['quotes:approve'],
    Permission['sales:read'],

    // Reports
    Permission['reports:view'],
    Permission['reports:export'],
    Permission['reports:advanced'],

    // Settings - Solo su compañía
    Permission['settings:read'],
    Permission['settings:update'],
  ],

  // ========== PROJECT_MANAGER ==========
  [UserRole.PROJECT_MANAGER]: [
    // Proyectos - Solo proyectos asignados
    Permission['projects:read'],
    Permission['projects:update'],

    // Stages & Blocks
    Permission['stages:read'],
    Permission['stages:create'],
    Permission['stages:update'],
    Permission['stages:delete'],
    Permission['blocks:read'],
    Permission['blocks:create'],
    Permission['blocks:update'],
    Permission['blocks:delete'],

    // Units
    Permission['units:read'],
    Permission['units:create'],
    Permission['units:update'],
    Permission['units:bulk_create'],
    Permission['units:change_status'],

    // Typologies & Amenities
    Permission['typologies:read'],
    Permission['typologies:create'],
    Permission['typologies:update'],
    Permission['amenities:read'],
    Permission['amenities:create'],
    Permission['amenities:update'],

    // Price Lists
    Permission['price_lists:read'],
    Permission['price_lists:create'],
    Permission['price_lists:update'],

    // Media
    Permission['media:read'],
    Permission['media:upload'],
    Permission['media:update'],
    Permission['media:delete'],

    // Sales - Lectura
    Permission['leads:read'],
    Permission['quotes:read'],
    Permission['sales:read'],

    // Reports
    Permission['reports:view'],
    Permission['reports:export'],

    // Settings - Lectura
    Permission['settings:read'],
  ],

  // ========== SALES_MANAGER ==========
  [UserRole.SALES_MANAGER]: [
    // Proyectos - Solo lectura
    Permission['projects:read'],
    Permission['units:read'],
    Permission['typologies:read'],
    Permission['amenities:read'],
    Permission['price_lists:read'],
    Permission['media:read'],

    // Sales - FULL ACCESS
    Permission['leads:read'],
    Permission['leads:create'],
    Permission['leads:update'],
    Permission['leads:assign'],
    Permission['quotes:read'],
    Permission['quotes:create'],
    Permission['quotes:update'],
    Permission['quotes:approve'],
    Permission['sales:read'],
    Permission['sales:create'],
    Permission['sales:update'],

    // Reports
    Permission['reports:view'],
    Permission['reports:export'],
    Permission['reports:advanced'],

    // Settings - Lectura
    Permission['settings:read'],
  ],

  // ========== SALES_AGENT ==========
  [UserRole.SALES_AGENT]: [
    // Proyectos - Solo lectura
    Permission['projects:read'],
    Permission['units:read'],
    Permission['typologies:read'],
    Permission['amenities:read'],
    Permission['price_lists:read'],
    Permission['media:read'],

    // Sales - Limitado
    Permission['leads:read'],
    Permission['leads:create'],
    Permission['leads:update'],
    Permission['quotes:read'],
    Permission['quotes:create'],
    Permission['quotes:update'],
    Permission['sales:read'],

    // Reports - Solo sus propios datos
    Permission['reports:view'],

    // Settings - Lectura
    Permission['settings:read'],
  ],

  // ========== MARKETING ==========
  [UserRole.MARKETING]: [
    // Proyectos - Solo lectura
    Permission['projects:read'],
    Permission['units:read'],
    Permission['typologies:read'],
    Permission['amenities:read'],
    Permission['media:read'],
    Permission['media:upload'],

    // Leads - Solo lectura
    Permission['leads:read'],

    // Reports
    Permission['reports:view'],
    Permission['reports:export'],

    // Settings - Lectura
    Permission['settings:read'],
  ],

  // ========== VIEWER ==========
  [UserRole.VIEWER]: [
    // Solo lectura de todo
    Permission['projects:read'],
    Permission['units:read'],
    Permission['typologies:read'],
    Permission['amenities:read'],
    Permission['price_lists:read'],
    Permission['media:read'],
    Permission['leads:read'],
    Permission['quotes:read'],
    Permission['sales:read'],
    Permission['reports:view'],
    Permission['settings:read'],
  ],

  // ========== LEGACY ROLES (compatibilidad) ==========
  [UserRole.PRESIDENT]: [
    // Equivalente a COMPANY_ADMIN
    Permission['projects:read'],
    Permission['projects:create'],
    Permission['projects:update'],
    Permission['leads:read'],
    Permission['leads:create'],
    Permission['leads:update'],
    Permission['reports:view'],
    Permission['reports:export'],
    Permission['reports:advanced'],
    Permission['settings:read'],
    Permission['settings:update'],
  ],

  [UserRole.MANAGER]: [
    // Equivalente a SALES_MANAGER
    Permission['projects:read'],
    Permission['units:read'],
    Permission['leads:read'],
    Permission['leads:create'],
    Permission['leads:update'],
    Permission['leads:assign'],
    Permission['quotes:read'],
    Permission['quotes:create'],
    Permission['quotes:approve'],
    Permission['sales:read'],
    Permission['reports:view'],
    Permission['reports:export'],
  ],

  [UserRole.AGENT]: [
    // Equivalente a SALES_AGENT
    Permission['projects:read'],
    Permission['units:read'],
    Permission['leads:read'],
    Permission['leads:create'],
    Permission['leads:update'],
    Permission['quotes:read'],
    Permission['quotes:create'],
    Permission['sales:read'],
    Permission['reports:view'],
  ],
};

// ===========================
// TYPE GUARDS & HELPERS
// ===========================

export const isSGIRole = (role: string): role is UserRole => {
  return Object.values(UserRole).includes(role as UserRole);
};

export const hasPermission = (role: UserRole, permission: Permission): boolean => {
  const rolePermissions = ROLE_PERMISSIONS[role];
  return rolePermissions.includes(permission);
};

export const hasAnyPermission = (role: UserRole, permissions: Permission[]): boolean => {
  return permissions.some(permission => hasPermission(role, permission));
};

export const hasAllPermissions = (role: UserRole, permissions: Permission[]): boolean => {
  return permissions.every(permission => hasPermission(role, permission));
};

// Role hierarchy helpers
export const isOwlyAdmin = (role: UserRole): boolean => role === UserRole.OWLY_ADMIN;
export const isCompanyAdmin = (role: UserRole): boolean => role === UserRole.COMPANY_ADMIN;
export const isProjectManager = (role: UserRole): boolean => role === UserRole.PROJECT_MANAGER;
export const isSalesManager = (role: UserRole): boolean => role === UserRole.SALES_MANAGER;
export const isSalesAgent = (role: UserRole): boolean => role === UserRole.SALES_AGENT;

// Check if user can manage companies
export const canManageCompanies = (role: UserRole): boolean => {
  return hasPermission(role, Permission['companies:create']);
};

// Check if user can manage projects
export const canManageProjects = (role: UserRole): boolean => {
  return hasPermission(role, Permission['projects:create']);
};

// Check if user has access to global config
export const hasGlobalConfigAccess = (role: UserRole): boolean => {
  return hasPermission(role, Permission['global_config:read']);
};

// Get role display name
export const getRoleDisplayName = (role: UserRole): string => {
  const roleNames: Record<UserRole, string> = {
    [UserRole.OWLY_ADMIN]: 'Administrador OWLY',
    [UserRole.COMPANY_ADMIN]: 'Administrador de Compañía',
    [UserRole.PROJECT_MANAGER]: 'Gestor de Proyectos',
    [UserRole.SALES_MANAGER]: 'Gerente de Ventas',
    [UserRole.SALES_AGENT]: 'Asesor de Ventas',
    [UserRole.MARKETING]: 'Marketing',
    [UserRole.VIEWER]: 'Visualizador',
    [UserRole.PRESIDENT]: 'Presidente',
    [UserRole.MANAGER]: 'Gerente',
    [UserRole.AGENT]: 'Agente',
  };

  return roleNames[role] || role;
};
